import React, { useEffect } from 'react';
import { Route, Redirect } from 'react-router';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useAuth0 } from 'auth0Provider';
import { isIE } from 'react-device-detect';
import { decodeURL } from 'utils/common';
import Loader from 'components/Loader';
import { selectIsPageNotFound } from 'containers/Landing/selectors';
import { PrivateRoute, AnonymousRoute } from 'containers/Authentication';
import {
  selectIsAuthenticated,
  selectIsAuthenticating,
  selectIsLogoutInitiated,
  selectIsExcluded,
  selectIsMigrated,
} from 'containers/Authentication/selectors';

const RouteBuilder = ({
  component: ChildComponent,
  layout: Layout,
  privateRoute = false,
  anonymousOnly = false,
  isAuthenticated,
  campaignId,
  useInSelfServe = false,
  isAuthenticating = false,
  isLogoutInitiated = false,
  isExcluded = false,
  isFullyMigrated = false,
  path,
  isPageNotFound = false,
  ...otherProps
}) => {
  const { loginWithRedirect, loading, logout } = useAuth0();
  const {
    initiateLogin,
    redirectToPath,
    isEmailRedirect,
    isCreateAccount,
    firstName,
    lastName,
    accountNumber,
    emailId,
    userToken,
    utmQuery,
  } = decodeURL();

  const loginRedirect = initiateLogin && !loading && !isAuthenticated;

  const getUtmQuery = (inPath) => {
    const hasQueryParams = inPath && inPath.includes('?');
    return utmQuery ? `${hasQueryParams ? '&' : '?'}${utmQuery}` : '';
  };

  useEffect(() => {
    if (loginRedirect && !isAuthenticating && !isLogoutInitiated) {
      const params = isCreateAccount
        ? {
            showForm: 'registration',
            firstName,
            lastName,
            accountNumber,
            emailId,
            userToken,
          }
        : {
            redirectToPath: `${window.location.pathname}${window.location.search}`,
          };
      loginWithRedirect(params);
    }

    if (isExcluded) {
      logout();
    }
  }, [loginRedirect, isAuthenticating, isLogoutInitiated]);

  if (loginRedirect || isAuthenticating) {
    return <Loader />;
  }

  if (isPageNotFound) {
    return (
      <Redirect
        to={{
          pathname: '/page-not-found',
          state: { prevPath: location.pathname },
        }}
      />
    );
  }

  const renderedChildComponent = (props) =>
    Layout ? (
      <Layout name={path}>
        <ChildComponent {...props} />
      </Layout>
    ) : (
      <ChildComponent {...props} />
    );
  const { location } = { ...otherProps };
  const routePath = `${path}${getUtmQuery(path)}`;

  if (anonymousOnly) {
    return (
      <AnonymousRoute
        {...otherProps}
        path={routePath}
        render={renderedChildComponent}
      />
    );
  }

  if (privateRoute) {
    return (
      <PrivateRoute
        {...otherProps}
        path={routePath}
        render={renderedChildComponent}
      />
    );
  }

  if (isAuthenticated && !useInSelfServe) {
    const redirectPath = (() => {
      if (isIE) return '/unsupported-browser';
      if (isFullyMigrated) return '/acct-xfer';
      if (isEmailRedirect) {
        return `${redirectToPath}${getUtmQuery(redirectToPath)}`;
      }
      return `/account/next-delivery${utmQuery && `?${utmQuery}`}`;
    })();

    return (
      <Redirect
        to={{
          pathname: redirectPath,
          state: { prevPath: location.pathname },
        }}
      />
    );
  }

  const isRootPath = path?.trim() === '/';

  if (!isAuthenticated && !isRootPath && !useInSelfServe) {
    return (
      <Redirect
        to={{
          pathname: '/',
          state: { prevPath: location.pathname },
        }}
      />
    );
  }

  return (
    <Route {...otherProps} path={routePath} render={renderedChildComponent} />
  );
};

const mapStateToProps = createStructuredSelector({
  isAuthenticated: selectIsAuthenticated(),
  isAuthenticating: selectIsAuthenticating(),
  isLogoutInitiated: selectIsLogoutInitiated(),
  isExcluded: selectIsExcluded(),
  isFullyMigrated: selectIsMigrated(),
  isPageNotFound: selectIsPageNotFound(),
});

export default connect(mapStateToProps, null)(RouteBuilder);
